import type { FollowUpActionResponseDto } from "@/generated/model/followUpActionResponseDto";
import { FollowUpActionResponseDtoDueStatus } from "@/generated/model/followUpActionResponseDtoDueStatus";
import type { SortDir } from "@/lib/category-display";
import type { FollowUpActionsTab } from "@/lib/follow-up-actions/follow-up-actions-list";
import { NURSERY_ROLE_LABELS } from "@/lib/roles";
import type { StatusPill } from "@/lib/workspace/status-pill";

/**
 * The four Task Manager tabs. Follow-up actions is the default.
 *
 * `tasks` holds manager-created general tasks, which have no backend yet, so it
 * maps to no API tab and renders an empty state without issuing a request.
 */
export type TaskManagerTab =
  | "follow-up-actions"
  | "tasks"
  | "awaiting-acknowledgement"
  | "history";

export const VALID_TABS: TaskManagerTab[] = [
  "follow-up-actions",
  "tasks",
  "awaiting-acknowledgement",
  "history",
];

export const DEFAULT_TAB: TaskManagerTab = "follow-up-actions";

/**
 * The API tab each Task Manager tab reads from, or `null` when the tab has no
 * server-side source yet.
 */
export function apiTabFor(tab: TaskManagerTab): FollowUpActionsTab | null {
  switch (tab) {
    case "follow-up-actions":
      return "open";
    case "awaiting-acknowledgement":
      return "requires-sign-off";
    case "history":
      return "history";
    case "tasks":
      return null;
  }
}

/** Sort columns supported server-side (must match the API allow-list). */
export type TaskManagerSortColumn =
  | "action"
  | "category"
  | "type"
  | "dueDate"
  | "assignee"
  | "dueStatus"
  | "completedAt";

/**
 * The date column a tab sorts on. History shows the completion timestamp rather
 * than the due date, so sorting it by `dueDate` would order rows by a value the
 * column isn't displaying.
 */
export function dateSortColumnFor(tab: TaskManagerTab): TaskManagerSortColumn {
  return tab === "history" ? "completedAt" : "dueDate";
}

export const PAGE_SIZE = 10;

/** Default sort per tab when the user hasn't picked a column. */
export const DEFAULT_SORT: Record<
  TaskManagerTab,
  { column: TaskManagerSortColumn; dir: SortDir }
> = {
  "follow-up-actions": { column: "dueDate", dir: "asc" },
  tasks: { column: "dueDate", dir: "asc" },
  "awaiting-acknowledgement": { column: "dueDate", dir: "asc" },
  history: { column: "completedAt", dir: "desc" },
};

/**
 * Tabs where a due-status filter is meaningful. Completed, cancelled and
 * awaiting-sign-off actions carry no due bucket, so on those tabs the control
 * would match nothing whatever the user picked.
 */
export function supportsDueStatusFilter(tab: TaskManagerTab): boolean {
  return tab === "follow-up-actions" || tab === "tasks";
}

/**
 * What the pagination footer calls the rows on each tab. The follow-up actions
 * tab lists actions specifically, so calling them "tasks" there would rename the
 * thing the tab is named after; the mixed and general tabs read as tasks.
 */
export function itemNounFor(tab: TaskManagerTab): string {
  return tab === "follow-up-actions" ? "actions" : "tasks";
}

/** Sentinel category filter value for actions with no primary category. */
export const UNCATEGORISED_CATEGORY_ID = "UNCATEGORISED";

/** Sentinel select value meaning "no filter applied". */
export const ALL_FILTER_VALUE = "ALL";

export const DUE_STATUS_OPTIONS: {
  value: FollowUpActionResponseDtoDueStatus;
  label: string;
}[] = [
  { value: FollowUpActionResponseDtoDueStatus.OVERDUE, label: "Overdue" },
  { value: FollowUpActionResponseDtoDueStatus.DUE_SOON, label: "Due soon" },
  { value: FollowUpActionResponseDtoDueStatus.OPEN, label: "Open" },
];

/**
 * The Status pill for a task row. Open is deliberately uncoloured, and rows with
 * no due bucket (completed, cancelled, awaiting sign-off) get no pill at all.
 *
 * Separate from `followUpActionPill`, which labels the same state "Late" on the
 * Workspace and Follow-up Actions surfaces — the two vocabularies are pending
 * consolidation.
 */
export function taskDueStatusPill(
  dueStatus: FollowUpActionResponseDtoDueStatus | undefined,
): StatusPill | null {
  switch (dueStatus) {
    case FollowUpActionResponseDtoDueStatus.OVERDUE:
      return { tone: "error", label: "Overdue", icon: "alert" };
    case FollowUpActionResponseDtoDueStatus.DUE_SOON:
      return { tone: "warning", label: "Due soon" };
    case FollowUpActionResponseDtoDueStatus.OPEN:
      return { tone: "neutral", label: "Open" };
    default:
      return null;
  }
}

/** "Jane Doe", else the role the action is assigned to, else a dash. */
export function assigneeLabel(action: FollowUpActionResponseDto): string {
  if (action.assignee) {
    const name =
      `${action.assignee.firstName ?? ""} ${action.assignee.lastName ?? ""}`.trim();
    return name || action.assignee.email;
  }
  if (action.assigneeRole) {
    return NURSERY_ROLE_LABELS[action.assigneeRole] ?? action.assigneeRole;
  }
  return "—";
}

/**
 * Who completed a task, matching the platform convention (name, else email).
 * A completed action always records the completer's id, so an absent
 * `completedBy` alongside a set `completedByUserId` means the user was
 * soft-deleted — named as such rather than blanked, which would read as
 * "nobody completed this". Cancelled actions reach the History tab carrying
 * neither, and get the dash.
 */
export function completedByLabel(action: FollowUpActionResponseDto): string {
  if (action.completedBy) {
    return (
      [action.completedBy.firstName, action.completedBy.lastName]
        .filter(Boolean)
        .join(" ")
        .trim() || action.completedBy.email
    );
  }
  return action.completedByUserId ? "Unknown user" : "—";
}

/**
 * The "Assigned to" filter selection. An action is assigned to a person or to a
 * role, never both, so the two are separate wire params — but a single dropdown
 * offers both, which is what these codecs bridge.
 */
export type AssigneeFilter =
  | { kind: "user"; userId: string }
  | { kind: "role"; role: string };

const USER_PREFIX = "user:";
const ROLE_PREFIX = "role:";

/** Encode an assignee filter as a single select value. */
export function encodeAssigneeFilter(filter: AssigneeFilter): string {
  return filter.kind === "user"
    ? `${USER_PREFIX}${filter.userId}`
    : `${ROLE_PREFIX}${filter.role}`;
}

/** Decode a select value back into an assignee filter, or null for "all". */
export function decodeAssigneeFilter(
  value: string | undefined,
): AssigneeFilter | null {
  if (!value || value === ALL_FILTER_VALUE) return null;
  if (value.startsWith(USER_PREFIX)) {
    const userId = value.slice(USER_PREFIX.length);
    return userId ? { kind: "user", userId } : null;
  }
  if (value.startsWith(ROLE_PREFIX)) {
    const role = value.slice(ROLE_PREFIX.length);
    return role ? { kind: "role", role } : null;
  }
  return null;
}

/** Label for a role option in the Assigned to dropdown. */
export function roleFilterLabel(role: string): string {
  return `Any ${NURSERY_ROLE_LABELS[role] ?? role}`;
}

/** The filter state the Reset filters button clears. */
export interface TaskManagerFilters {
  categoryId?: string;
  assignee?: string;
  dueStatus?: FollowUpActionResponseDtoDueStatus;
  search: string;
}

/**
 * The cleared filter state. Location is absent by design: that control selects
 * the nursery for the whole session (the same state the top-bar switcher writes),
 * so resetting filters must not relocate the user.
 */
export const EMPTY_FILTERS: TaskManagerFilters = {
  categoryId: undefined,
  assignee: undefined,
  dueStatus: undefined,
  search: "",
};

/** Whether anything is filtered, so Reset can be disabled when it would no-op. */
export function hasActiveFilters(filters: TaskManagerFilters): boolean {
  return (
    !!filters.categoryId ||
    !!filters.assignee ||
    !!filters.dueStatus ||
    filters.search.trim().length > 0
  );
}
